import { Component, OnInit, OnDestroy, ViewChild, ApplicationRef } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { concat, first, interval, merge } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { Logger, UntilDestroy, untilDestroyed } from '@shared';
import { I18nService } from '@app/i18n';
import { MatSidenav } from '@angular/material/sidenav';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SwUpdate } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';

const log = new Logger('App');

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav', { static: false }) sidenav!: MatSidenav;

  language: string = '';
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslocoService,
    private i18nService: I18nService,
    private breakpoint: BreakpointObserver,
    private metaTagService: Meta,
    private appRef: ApplicationRef,
    private updates: SwUpdate,
    private snackbar: MatSnackBar,
  ) {
    // Allow the app to stabilize first, before starting
    // polling for updates with `interval()`.
    // For the service worker
    // if (environment.production) {
    //   const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
    //   const everyTenMinutes$ = interval(10 * 60 * 1000);
    //   const everyTenMinutesOnceAppIsStable$ = concat(appIsStable$, everyTenMinutes$);
    //   everyTenMinutesOnceAppIsStable$.subscribe(async () => {
    //     try {
    //       const updateFound = await this.updates.checkForUpdate();
    //       console.log(updateFound ? 'A new version is available.' : 'Already on the latest version.');
    //       if (updateFound) {
    //         let snack = this.snackbar.open(
    //           this.translateService.translate('global.messages.update.text'),
    //           this.translateService.translate('global.messages.update.action'),
    //         );
    //         snack.onAction().subscribe(() => {
    //           window.location.reload();
    //         });
    //         setTimeout(() => {
    //           snack.dismiss();
    //         }, 10000);
    //       }
    //     } catch (err) {
    //       console.error('Failed to check for updates:', err);
    //     }
    //   });
    // }
  }

  ngOnInit() {
    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    log.debug('init');
    this.invokeMetaTags();
    // Setup translations
    this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);

    const onNavigationEnd = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.langChanges$, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
        untilDestroyed(this),
      )
      .subscribe((event) => {
        const title = event['title'];
        if (title) {
          this.titleService.setTitle(this.translateService.translate(title));
          // console.warn(this.i18nService.language);
          this.language = this.i18nService.language.endsWith('-CH')
            ? this.i18nService.language.split('-')[0]
            : this.i18nService.language;
        }
      });
    this.breakpoint
      .observe([Breakpoints.Small, Breakpoints.XSmall])
      .pipe(
        filter(({ matches }) => !matches),
        untilDestroyed(this),
      )
      .subscribe(() => this.sidenav?.close());
  }

  ngOnDestroy() {
    this.i18nService.destroy();
  }

  invokeMetaTags() {
    this.metaTagService.addTags([
      {
        name: 'description',
        content:
          'Die innovative Lösung zur Baukostenschätzung und Baukostenberechnung: Neubau-, Betriebs- und Umbaukosten. Jetzt 7 Tage gratis testen!',
      },
      { name: 'Keywords', content: 'Baukostenschätzung & Baukostenberechnung' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      //google
      { name: 'google', content: 'notranslate' },
      // Indexing / Spiders
      // 'googlebot': 'all',
      // 'bingbot': 'all',
      // 'robots': 'all',
      // OpenGraph
      { property: 'og:site_name', content: 'website' },
      { property: 'og:image', content: 'https://www.keevalue.ch/assets/images/background/og/opengraph_bg_1000.jpg' },
      {
        property: 'og:image:width',
        content: 'Die schweizerische & innovative Lösung zur Ermittlung von Neubau-, Betriebs- und Umbaukosten.',
      },
      {
        property: 'og:image:height',
        content: 'Die schweizerische & innovative Lösung zur Ermittlung von Neubau-, Betriebs- und Umbaukosten.',
      },
      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: '@kennwerte' },
      { name: 'twitter:image', content: 'https://www.keevalue.ch/assets/images/background/og/opengraph_bg_1000.jpg' },
    ]);
  }
}
